/**
 * Âmedorée — Help page and help drawer
 *
 * The questions live once, in the Help topic sections of the help page
 * (sections/amedoree-help-topic.liquid). This file reads them and builds:
 *
 *   - on the help page ([data-amd-help]): search, Most asked, Topics, the topic
 *     view and the answer drawer. #q-<handle> opens an answer, #topic-<handle>
 *     a topic, so support can link straight to an answer.
 *   - on product pages ([data-amd-help-mini]): a drawer with the questions picked
 *     in the block, a search over every answer, and the answer itself. The help
 *     page is fetched once, the first time the drawer opens.
 *   - on the contact page ([data-amd-help-inline]): a search and the most asked
 *     questions above the form, answers opening in a drawer.
 *
 * Searches and "Did this answer your question?" are published as custom events
 * (amd_help_search, amd_help_vote) so a custom pixel can collect them.
 */
(function () {
  'use strict';

  if (window.AmedoreeHelp) return;

  var ANIMATION_MS = 340;

  // Everyday words mapped to the words the answers use
  var SYNONYMS = {
    refund: ['refund', 'money'],
    money: ['refund'],
    ship: ['delivery', 'dispatch'],
    shipping: ['delivery', 'dispatch'],
    deliver: ['delivery'],
    customs: ['duties', 'import'],
    tax: ['duties', 'taxes'],
    taxes: ['duties'],
    vat: ['tax', 'duties'],
    swap: ['exchange'],
    size: ['size', 'fit'],
    fit: ['size', 'fit'],
    lost: ['delivered', 'missing'],
    late: ['delayed', 'tracking'],
    parcel: ['order'],
    package: ['order', 'parcel'],
    klarna: ['payment'],
    pay: ['payment']
  };

  var CHEVRON = '<svg class="amd-help__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg>';

  /* --- Data -------------------------------------------------------------- */

  function norm(value) {
    return String(value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  function collect(root) {
    var topics = [];
    var byId = {};
    var all = [];

    root.querySelectorAll('[data-amd-help-topic]').forEach(function (section) {
      var topic = {
        handle: section.getAttribute('data-amd-help-topic'),
        name: section.getAttribute('data-name') || '',
        items: []
      };

      section.querySelectorAll('details[data-q]').forEach(function (details) {
        var answer = details.querySelector('.amd-help-q__answer');
        var copy = answer ? answer.cloneNode(true) : document.createElement('div');
        var fallback = copy.querySelector('.amd-help-q__fallback-action');
        if (fallback) fallback.remove();
        var summary = details.querySelector('summary');
        var href = details.getAttribute('data-action-href');

        var item = {
          id: details.getAttribute('data-q'),
          topic: topic,
          q: summary ? summary.textContent.trim() : '',
          html: copy.innerHTML.trim(),
          text: copy.textContent.replace(/\s+/g, ' ').trim(),
          keywords: details.getAttribute('data-keywords') || '',
          related: (details.getAttribute('data-related') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean),
          most: details.hasAttribute('data-most'),
          action: href ? {
            label: details.getAttribute('data-action-label') || '',
            href: href,
            style: details.getAttribute('data-action-style') || 'button'
          } : null
        };

        if (!item.id || !item.q) return;
        if (!byId[item.id]) byId[item.id] = item;
        topic.items.push(item);
        all.push(item);
      });

      if (topic.items.length) topics.push(topic);
    });

    return { topics: topics, byId: byId, all: all };
  }

  function wordsOf(query) {
    return norm(query).split(/[^a-z0-9]+/).filter(function (w) { return w.length > 1; });
  }

  // Every word must match the question, its search words, the topic or the answer
  function search(data, query) {
    var words = wordsOf(query);
    if (!words.length) return [];

    var scored = [];
    data.all.forEach(function (item, index) {
      var inQuestion = norm(item.q);
      var inKeywords = norm(item.keywords + ' ' + item.topic.name);
      var inAnswer = norm(item.text);
      var score = 0;

      for (var i = 0; i < words.length; i++) {
        var word = words[i];
        var variants = [word, word.replace(/s$/, '')].concat(SYNONYMS[word] || []);
        var best = 0;
        variants.forEach(function (v) {
          if (!v) return;
          if (inQuestion.indexOf(v) > -1) best = Math.max(best, 3);
          else if (inKeywords.indexOf(v) > -1) best = Math.max(best, 2);
          else if (inAnswer.indexOf(v) > -1) best = Math.max(best, 1);
        });
        if (!best) return;
        score += best;
      }

      scored.push({ item: item, score: score, index: index });
    });

    var seen = {};
    return scored
      .sort(function (a, b) { return b.score - a.score || a.index - b.index; })
      .map(function (s) { return s.item; })
      .filter(function (item) {
        if (seen[item.id]) return false;
        seen[item.id] = true;
        return true;
      })
      .slice(0, 10);
  }

  function publish(name, payload) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* analytics is optional */ }
  }

  /* --- Building blocks --------------------------------------------------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function highlight(target, text, words) {
    if (!words || !words.length) {
      target.textContent = text;
      return;
    }
    var pattern = new RegExp('(' + words.map(function (w) { return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|') + ')', 'gi');
    text.split(pattern).forEach(function (part, i) {
      if (!part) return;
      if (i % 2 === 1) target.appendChild(el('mark', '', part));
      else target.appendChild(document.createTextNode(part));
    });
  }

  function row(item, onOpen, options) {
    options = options || {};
    var button = el('button', 'amd-help__row');
    button.type = 'button';

    if (options.label || options.snippet) {
      var text = el('span', 'amd-help__row-text');
      if (options.label) text.appendChild(el('span', 'amd-help__label', item.topic.name));
      var q = el('span', 'amd-help__row-q');
      highlight(q, item.q, options.words);
      text.appendChild(q);
      if (options.snippet) text.appendChild(el('span', 'amd-help__row-snippet', item.text));
      button.appendChild(text);
    } else {
      var span = el('span', 'amd-help__row-q');
      span.style.flex = '1 1 auto';
      span.textContent = item.q;
      button.appendChild(span);
    }

    button.insertAdjacentHTML('beforeend', CHEVRON);
    button.addEventListener('click', function () { onOpen(item); });
    return button;
  }

  function fillAnswer(scope, item) {
    var topic = scope.querySelector('[data-amd-help-d-topic]');
    var q = scope.querySelector('[data-amd-help-d-q]');
    var a = scope.querySelector('[data-amd-help-d-a]');
    var button = scope.querySelector('[data-amd-help-d-button]');
    var link = scope.querySelector('[data-amd-help-d-link]');

    if (topic) topic.textContent = item.topic.name;
    if (q) q.textContent = item.q;
    if (a) a.innerHTML = item.html;

    [button, link].forEach(function (node) { if (node) node.hidden = true; });
    if (item.action) {
      var target = item.action.style === 'link' ? link : button;
      if (target) {
        target.textContent = item.action.label;
        target.setAttribute('href', item.action.href);
        target.hidden = false;
      }
    }
  }

  /* --- Dialog ------------------------------------------------------------ */

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function openDialog(dialog) {
    if (dialog.open) return;
    dialog.showModal();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { dialog.classList.add('is-open'); });
    });
  }

  function closeDialog(dialog, done) {
    if (!dialog.open) return;
    dialog.classList.remove('is-open');
    window.setTimeout(function () {
      if (dialog.open) dialog.close();
      if (done) done();
    }, reducedMotion() ? 0 : ANIMATION_MS);
  }

  function wireDialog(dialog, onClose) {
    if (dialog.__amdWired) return;
    dialog.__amdWired = true;

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.closest('[data-amd-help-close]')) {
        event.preventDefault();
        closeDialog(dialog, onClose);
      }
    });

    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeDialog(dialog, onClose);
    });
  }

  /* --- Help page --------------------------------------------------------- */

  function HelpPage(root) {
    this.root = root;
    this.input = root.querySelector('[data-amd-help-input]');
    this.clearButton = root.querySelector('[data-amd-help-clear]');
    this.home = root.querySelector('[data-amd-help-home]');
    this.results = root.querySelector('[data-amd-help-results]');
    this.topicView = root.querySelector('[data-amd-help-topic-view]');
    this.dialog = root.querySelector('[data-amd-help-dialog]');
    this.topic = '';
    this.query = '';
    this.searchTimer = null;
    this.bind();
    this.refresh();
  }

  HelpPage.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () {
      self.query = self.input.value;
      self.render();
      window.clearTimeout(self.searchTimer);
      self.searchTimer = window.setTimeout(function () {
        if (wordsOf(self.query).length) {
          publish('amd_help_search', { query: self.query.trim(), results: search(self.data, self.query).length });
        }
      }, 1200);
    });

    this.clearButton.addEventListener('click', function () {
      self.input.value = '';
      self.query = '';
      self.render();
      self.input.focus();
    });

    this.root.querySelector('[data-amd-help-back]').addEventListener('click', function () {
      self.showTopic('');
    });

    this.root.querySelectorAll('[data-amd-help-vote]').forEach(function (button) {
      button.addEventListener('click', function () {
        var vote = button.getAttribute('data-amd-help-vote');
        self.setVote(vote);
        if (self.current) publish('amd_help_vote', { question: self.current.id, helpful: vote === 'yes' });
      });
    });

    wireDialog(this.dialog, function () {
      self.current = null;
      if (/^#q-/.test(window.location.hash)) {
        history.replaceState(null, '', self.topic ? '#topic-' + self.topic : window.location.pathname + window.location.search);
      }
    });

    // Shortcuts and answer links like #topic-size-fit or #q-refund. Handled here
    // rather than through the hash change, which the theme's own anchor scrolling
    // can swallow.
    this.root.addEventListener('click', function (event) {
      var link = event.target.closest('a[href^="#topic-"], a[href^="#q-"]');
      if (!link || !self.root.contains(link)) return;
      event.preventDefault();
      event.stopPropagation();
      history.replaceState(null, '', link.getAttribute('href'));
      if (self.dialog.open && /^#topic-/.test(link.getAttribute('href'))) closeDialog(self.dialog);
      self.fromHash();
    });

    window.addEventListener('hashchange', function () { self.fromHash(); });
  };

  HelpPage.prototype.refresh = function () {
    this.data = collect(document);
    this.renderHome();
    this.render();
    this.fromHash();
    if (!(window.Shopify && Shopify.designMode)) {
      document.documentElement.classList.add('amd-help-built');
    }
  };

  HelpPage.prototype.fromHash = function () {
    var hash = decodeURIComponent(window.location.hash || '');
    var match;
    if ((match = hash.match(/^#topic-(.+)$/))) {
      this.showTopic(match[1]);
    } else if ((match = hash.match(/^#q-(.+)$/)) && this.data.byId[match[1]]) {
      this.open(this.data.byId[match[1]], true);
    }
  };

  HelpPage.prototype.showTopic = function (handle) {
    var exists = this.data.topics.some(function (t) { return t.handle === handle; });
    this.topic = exists ? handle : '';
    if (this.topic) {
      this.input.value = '';
      this.query = '';
    }
    if (!this.topic && /^#topic-/.test(window.location.hash)) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    this.render();
    if (this.topic) this.topicView.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  HelpPage.prototype.renderHome = function () {
    var self = this;
    var open = function (item) { self.open(item); };

    var most = this.root.querySelector('[data-amd-help-most]');
    var mostItems = this.data.all.filter(function (item) { return item.most; });
    most.replaceChildren.apply(most, mostItems.map(function (item) { return row(item, open); }));
    this.root.querySelector('[data-amd-help-most-wrap]').hidden = mostItems.length === 0;

    var topics = this.root.querySelector('[data-amd-help-topics]');
    topics.replaceChildren.apply(topics, this.data.topics.map(function (topic) {
      var card = el('div', 'amd-help__topic-card');

      var head = el('button', 'amd-help__topic-head-btn');
      head.type = 'button';
      head.appendChild(el('span', '', topic.name));
      head.appendChild(el('span', 'amd-help__count', String(topic.items.length)));
      head.insertAdjacentHTML('beforeend', CHEVRON);
      head.addEventListener('click', function () { self.showTopic(topic.handle); });
      card.appendChild(head);

      var preview = el('div', 'amd-help__topic-preview');
      topic.items.slice(0, 3).forEach(function (item) {
        var b = el('button', '', item.q);
        b.type = 'button';
        b.addEventListener('click', function () { self.open(item); });
        preview.appendChild(b);
      });
      card.appendChild(preview);

      var allButton = el('button', 'amd-help__topic-all', 'All ' + topic.items.length + ' answers');
      allButton.type = 'button';
      allButton.addEventListener('click', function () { self.showTopic(topic.handle); });
      card.appendChild(allButton);

      return card;
    }));
  };

  HelpPage.prototype.render = function () {
    var self = this;
    var open = function (item) { self.open(item); };
    var words = wordsOf(this.query);
    var searching = words.length > 0;

    this.clearButton.hidden = this.query === '';
    this.results.hidden = !searching;
    this.topicView.hidden = searching || !this.topic;
    this.home.hidden = searching || !!this.topic;

    if (searching) {
      var found = search(this.data, this.query);
      var phrase = '“' + this.query.trim() + '”';
      this.root.querySelector('[data-amd-help-count]').textContent = found.length
        ? found.length + (found.length === 1 ? ' answer for ' : ' answers for ') + phrase
        : 'No answer for ' + phrase;
      this.root.querySelector('[data-amd-help-empty]').hidden = found.length > 0;
      var list = this.root.querySelector('[data-amd-help-result-list]');
      list.hidden = found.length === 0;
      list.replaceChildren.apply(list, found.map(function (item) {
        return row(item, open, { label: true, snippet: true, words: words });
      }));
    }

    if (!searching && this.topic) {
      var topic = this.data.topics.filter(function (t) { return t.handle === self.topic; })[0];
      this.root.querySelector('[data-amd-help-topic-title]').textContent = topic.name;
      this.root.querySelector('[data-amd-help-topic-count]').textContent = topic.items.length + ' answers';
      var topicList = this.root.querySelector('[data-amd-help-topic-list]');
      topicList.replaceChildren.apply(topicList, topic.items.map(function (item) { return row(item, open); }));
    }
  };

  HelpPage.prototype.setVote = function (vote) {
    this.root.querySelectorAll('[data-amd-help-vote]').forEach(function (button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-amd-help-vote') === vote ? 'true' : 'false');
    });
    this.root.querySelector('[data-amd-help-vote-yes]').hidden = vote !== 'yes';
    this.root.querySelector('[data-amd-help-vote-no]').hidden = vote !== 'no';
  };

  HelpPage.prototype.open = function (item, fromHash) {
    var self = this;
    this.current = item;
    fillAnswer(this.dialog, item);
    this.setVote('');

    var related = item.related.map(function (id) { return self.data.byId[id]; }).filter(function (r) { return r && r !== item; });
    var relatedList = this.dialog.querySelector('[data-amd-help-d-related]');
    relatedList.replaceChildren.apply(relatedList, related.map(function (r) { return row(r, function (next) { self.open(next); }); }));
    this.dialog.querySelector('[data-amd-help-d-related-wrap]').hidden = related.length === 0;

    var body = this.dialog.querySelector('.amd-help-drawer__body');
    if (body) body.scrollTop = 0;

    if (!fromHash) history.replaceState(null, '', '#q-' + item.id);
    openDialog(this.dialog);
  };

  /* --- Product page drawer ----------------------------------------------- */

  var helpPageCache = {};

  function loadHelpData(url) {
    if (!helpPageCache[url]) {
      helpPageCache[url] = fetch(url, { credentials: 'same-origin' })
        .then(function (response) { return response.text(); })
        .then(function (html) { return collect(new DOMParser().parseFromString(html, 'text/html')); })
        .catch(function () { return { topics: [], byId: {}, all: [] }; });
    }
    return helpPageCache[url];
  }

  function HelpMini(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-amd-help-dialog]');
    this.input = root.querySelector('[data-amd-help-input]');
    this.list = root.querySelector('[data-amd-help-mini-list]');
    this.label = root.querySelector('[data-amd-help-mini-label]');
    this.defaultLabel = this.label.textContent;
    this.listView = root.querySelector('[data-amd-help-mini-list-view]');
    this.answerView = root.querySelector('[data-amd-help-mini-answer-view]');
    this.title = root.querySelector('[data-amd-help-mini-title]');
    this.back = root.querySelector('[data-amd-help-mini-back]');
    this.empty = root.querySelector('[data-amd-help-mini-empty]');
    this.handles = (root.getAttribute('data-questions') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    this.data = null;
    this.bind();
  }

  HelpMini.prototype.bind = function () {
    var self = this;
    this.input.addEventListener('input', function () { self.renderList(); });
    this.back.addEventListener('click', function () { self.showList(); });
    wireDialog(this.dialog, function () { self.showList(); });
  };

  HelpMini.prototype.open = function () {
    var self = this;
    this.showList();
    openDialog(this.dialog);
    if (!this.data) {
      this.label.textContent = 'Loading answers';
      loadHelpData(this.root.getAttribute('data-faq-url')).then(function (data) {
        self.data = data;
        self.label.textContent = self.defaultLabel;
        self.renderList();
      });
    }
  };

  HelpMini.prototype.renderList = function () {
    if (!this.data) return;
    var self = this;
    var query = this.input.value;
    var words = wordsOf(query);
    var items;

    if (words.length) {
      items = search(this.data, query);
      this.label.textContent = items.length + (items.length === 1 ? ' answer' : ' answers');
    } else {
      items = this.handles.map(function (h) { return self.data.byId[h]; }).filter(Boolean);
      if (!items.length) items = this.data.all.filter(function (item) { return item.most; });
      this.label.textContent = this.defaultLabel;
    }

    this.empty.hidden = !(words.length && items.length === 0);
    this.list.replaceChildren.apply(this.list, items.map(function (item) {
      return row(item, function (next) { self.showAnswer(next); }, words.length ? { label: true, words: words } : null);
    }));
  };

  HelpMini.prototype.showAnswer = function (item) {
    fillAnswer(this.answerView, item);
    this.listView.hidden = true;
    this.answerView.hidden = false;
    this.title.hidden = true;
    this.back.hidden = false;
    var body = this.dialog.querySelector('.amd-help-drawer__body');
    if (body) body.scrollTop = 0;
  };

  HelpMini.prototype.showList = function () {
    this.listView.hidden = false;
    this.answerView.hidden = true;
    this.title.hidden = false;
    this.back.hidden = true;
  };

  /* --- Contact page: answers before the form ---------------------------- */

  function HelpInline(root) {
    var self = this;
    this.root = root;
    this.input = root.querySelector('[data-amd-help-input]');
    this.list = root.querySelector('[data-amd-help-inline-list]');
    this.label = root.querySelector('[data-amd-help-inline-label]');
    this.defaultLabel = this.label.textContent;
    this.empty = root.querySelector('[data-amd-help-inline-empty]');
    this.dialog = root.querySelector('[data-amd-help-dialog]');
    this.handles = (root.getAttribute('data-questions') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    this.data = null;

    wireDialog(this.dialog);
    this.input.addEventListener('input', function () { self.render(); });

    // "Still unsure? Write to us" in the drawer: close it and go to the form below
    this.dialog.addEventListener('click', function (event) {
      if (!event.target.closest('[data-amd-help-to-form]')) return;
      var field = document.querySelector('form input[name="form_type"][value="contact"]');
      var form = field && field.closest('form');
      if (!form) return;
      event.preventDefault();
      closeDialog(self.dialog, function () {
        form.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
        var first = form.querySelector('input:not([type="hidden"]), textarea');
        if (first) first.focus({ preventScroll: true });
      });
    });

    this.label.textContent = 'Loading answers';
    loadHelpData(root.getAttribute('data-faq-url')).then(function (data) {
      self.data = data;
      self.label.textContent = self.defaultLabel;
      if (!data.all.length) {
        root.querySelector('[data-amd-help-inline-answers]').hidden = true;
        return;
      }
      self.render();
    });
  }

  HelpInline.prototype.render = function () {
    if (!this.data) return;
    var self = this;
    var query = this.input.value;
    var words = wordsOf(query);
    var items;

    if (words.length) {
      items = search(this.data, query).slice(0, 6);
      this.label.textContent = items.length ? items.length + (items.length === 1 ? ' answer' : ' answers') : 'No answer yet';
    } else {
      items = this.handles.map(function (h) { return self.data.byId[h]; }).filter(Boolean);
      if (!items.length) items = this.data.all.filter(function (item) { return item.most; });
      this.label.textContent = this.defaultLabel;
    }

    this.empty.hidden = !(words.length && items.length === 0);
    this.list.hidden = items.length === 0;
    this.list.replaceChildren.apply(this.list, items.map(function (item) {
      return row(item, function (next) {
        fillAnswer(self.dialog, next);
        var body = self.dialog.querySelector('.amd-help-drawer__body');
        if (body) body.scrollTop = 0;
        openDialog(self.dialog);
      }, words.length ? { label: true, words: words } : null);
    }));
  };

  /* --- Start ------------------------------------------------------------- */

  var pages = [];

  function initPages() {
    document.querySelectorAll('[data-amd-help-inline]').forEach(function (root) {
      if (!root.__amdHelpInline) root.__amdHelpInline = new HelpInline(root);
    });

    document.querySelectorAll('[data-amd-help]').forEach(function (root) {
      if (root.__amdHelp) {
        root.__amdHelp.refresh();
        return;
      }
      root.__amdHelp = new HelpPage(root);
      pages.push(root.__amdHelp);
    });
  }

  // Product drawers: the product block can be re-rendered on variant change, so
  // drawers are found from the click rather than bound once
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-amd-help-mini-open]');
    if (!trigger) return;
    var root = trigger.closest('[data-amd-help-mini]');
    if (!root) return;
    event.preventDefault();
    if (!root.__amdHelpMini) root.__amdHelpMini = new HelpMini(root);
    root.__amdHelpMini.open();
  });

  // Theme editor: rebuild when a section or block changes
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder', 'shopify:block:select'].forEach(function (name) {
    document.addEventListener(name, function () { window.setTimeout(initPages, 0); });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPages);
  } else {
    initPages();
  }

  window.AmedoreeHelp = { collect: collect, search: search, refresh: initPages };
})();
